import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Sparkles, CalendarDays, Star, Award, Heart, Users, Flame } from "lucide-react";
import StoryPlayer from "./StoryPlayer";
import StoryStrip from "./StoryStrip";
import api from "../lib/api";

const sum = (cards, k) => cards.reduce((a, c) => a + (c[k] || 0), 0);

export const buildFamilySlides = (cards, year) => {
  const pandas = cards.map((c) => c.child.panda);
  const names = cards.map((c) => c.child.name);
  const family = names.length === 2 ? names.join(" & ") : `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
  const perChild = cards.flatMap((d) => {
    const certs = d.lessons.filter((l) => l.certified_this_year).length;
    return [
      { bg: "#63c79a", icon: CalendarDays, panda: d.child.panda, title: `${d.child.name} showed up`, big: `${d.active_days}`, sub: `active day${d.active_days === 1 ? "" : "s"} · longest streak ${d.longest_streak_weeks} week${d.longest_streak_weeks === 1 ? "" : "s"}` },
      { bg: "#f3c932", icon: Star, panda: d.child.panda, item: "coin", title: `${d.child.name}'s bamboo`, big: `${d.bamboo_year}`, sub: `bamboo · ${d.stickers_earned} sticker${d.stickers_earned === 1 ? "" : "s"}${d.goal_badge ? ` · ${d.goal_badge.label} badge` : ""}`, dark: true },
      { bg: "#1b8bc9", icon: Award, panda: d.child.panda, title: `${d.child.name}'s lessons`, big: `${certs}`, sub: `certificate${certs === 1 ? "" : "s"} in ${year} · ${d.cheers_year} cheer${d.cheers_year === 1 ? "" : "s"} received${d.quiz_average !== null ? ` · quiz avg ${d.quiz_average}/5` : ""}` },
    ];
  });
  const leader = [...cards].sort((a, b) => b.bamboo_year - a.bamboo_year)[0];
  const quiet = cards.every((d) => d.active_days === 0 && d.bamboo_year === 0);
  return [
    { bg: "#7d5fb2", icon: Sparkles, pandas, title: `The ${year} family story`, big: family, sub: quiet ? `${year} was before your family started - this year's story is yours to write!` : `${names.length} learners, one family - here's the year you built together.` },
    ...perChild,
    { bg: "#f7931e", icon: Flame, pandas, title: "Together", big: `${sum(cards, "active_days")}`, sub: `family learning days · ${sum(cards, "bamboo_year")} bamboo · ${cards.reduce((a, d) => a + d.lessons.filter((l) => l.certified_this_year).length, 0)} certificates · ${sum(cards, "cheers_year")} cheers` },
    { bg: "#0c4a6e", icon: Users, pandas, title: "Proud of you all!", big: leader && leader.bamboo_year > 0 ? `${leader.child.name} led the bamboo board` : "Well done", sub: "Every report card is ready on the certificate wall.", last: true, cta: "See the report cards" },
  ];
};

// Shared year-in-review for the whole family (all own children back to back). Opens via /family?story=1.
const FamilyYearStory = ({ children, year: yearProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cards, setCards] = useState(null);
  const open = new URLSearchParams(location.search).get("story") === "1";
  const year = Number(new URLSearchParams(location.search).get("year")) || yearProp || new Date().getFullYear() - 1;
  const ids = children.map((c) => c.id).join(",");

  useEffect(() => {
    if (!open || !ids) return;
    setCards(null);
    Promise.all(ids.split(",").map((id) => api.get(`/children/${id}/report-card?year=${year}`).then((r) => r.data))).then(setCards).catch(() => setCards([]));
  }, [open, ids, year]);

  const close = () => navigate({ pathname: location.pathname, hash: location.hash }, { replace: true });
  const stripRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const slides = cards && cards.length ? buildFamilySlides(cards, year) : [];

  // Save the story as one PNG strip: native share sheet when available, otherwise download.
  const save = async () => {
    setSaving(true);
    try {
      const dataUrl = await toPng(stripRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#f7fdfe", skipFonts: true });
      const file = new File([await (await fetch(dataUrl)).blob()], `family-year-story-${year}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        try { await navigator.share({ files: [file], title: `Our ${year} family story`, text: `Our ${year} year on Teach Your Child Credit` }); toast.success("Story shared"); return; } catch (e) { if (e?.name === "AbortError") return; }
      }
      const a = document.createElement("a");
      a.href = dataUrl; a.download = file.name; a.click();
      toast.success("Story image saved", { description: "Send it to grandparents from your photos or downloads." });
    } catch (e) { toast.error("Could not create the image", { description: e?.message }); } finally { setSaving(false); }
  };

  if (!open) return null;
  if (!cards) return createPortal(<div className="fixed inset-0 z-[100] bg-[#7d5fb2]" style={{ margin: 0 }} data-testid="family-story-loading" />, document.body);
  if (cards.length < 1) return null;
  return (
    <>
      <StoryStrip ref={stripRef} slides={slides} heading={`Our ${year} family story`} />
      <StoryPlayer slides={slides} onClose={close} onFinish={() => { close(); navigate("/certificates"); }} onSave={save} saving={saving} testId="family-story" />
    </>
  );
};

export default FamilyYearStory;
